import React from "react";
import {img} from "@/utils/pluginImg.js";
import {WHY_PERKS} from "@/constants/airportPerks.js";
import TrackDriverButton from "./TrackDriverButton.jsx";

// "More Than A Ride. Why Choose us?" — the content behind the "Exclusive Airport
// Pickup Perks" bar. Shared between the booking flow (compare modal / mobile step,
// which pass onPerkAction to make perks clickable) and the trip edit page (which
// renders it static, without onPerkAction). `.why-choose-us` CSS is top-level, so
// no wrapper class is required.
// ONE perk row. Extracted so a single perk can be rendered on its own without copying
// this markup — the /help FAQ ("Do you support oversized luggage…") shows just the
// Oversized row, and must stay identical to the one in this list.
// Must sit inside a `.wcu-perks` <ul> for the styles to apply.
/**
 * `subOnly`: drop the row's icon and title, keeping just the description (and any
 * bags/chevron). For the /help FAQ, where the card header already carries both — the
 * same icon, and a question that says what the row's title would — so repeating them
 * inside the answer is noise.
 */
export function PerkRow({perk: p, onPerkAction, subOnly = false}) {
    return (
        <li className={`${p.verified ? "is-verified" : ""}${p.action ? " is-clickable" : ""}${subOnly ? " is-sub-only" : ""}`}
            onClick={p.action && onPerkAction ? () => onPerkAction(p.action) : undefined}>
            {!subOnly && <span className={"ic" + (p.round ? " round" : "")}><img src={img(p.icon)} alt=""/></span>}
            <span className="txt">
                {subOnly ? null : p.verified ? (
                    <span className="wcu-perk-head">
                        <strong>{p.title}</strong>
                        <span className="wcu-verified">
                            Identity verified
                            <img src={img("verification.svg")} alt=""/>
                        </span>
                    </span>
                ) : (
                    <strong>{p.title}</strong>
                )}
                {p.km ? (
                    <small className="wcu-sub-split">
                        <span className="km">{p.km}</span>
                        <span>{p.sub}</span>
                    </small>
                ) : (p.sub && <small>{p.sub}</small>)}
            </span>
            {p.bags && (
                <span className="wcu-bags">
                    <img src={img("oversize-skis.png")} alt=""/>
                    <img src={img("oversize-duffel.png")} alt=""/>
                    <img src={img("oversize-surfboard.png")} alt=""/>
                    <img src={img("oversize-golf.png")} alt=""/>
                    <img src={img("oversize-guitar.png")} alt=""/>
                </span>
            )}
            {p.chevron && <img className="chev" src={img("listing-arrow.svg")} alt=""/>}
        </li>
    );
}

export default function WhyChooseUs({onPerkAction}) {
    return (
        <div className="why-choose-us">
            <h3 className="wcu-title">More Than A Ride. Why Choose us?</h3>

            <div className="wcu-stats">
                <div className="wcu-stat">
                    <div className="wcu-stat-row">
                        <img src={img("persons-icons.svg")} alt=""/>
                        <div className="wcu-stat-text">
                            <strong>10,000+</strong>
                            <small>Rides completed</small>
                        </div>
                    </div>
                    <span className="wcu-badge">Over 18 Years in Business</span>
                </div>
                <div className="wcu-stat">
                    <div className="wcu-stat-row">
                        <img src={img("slider-shild.svg")} alt=""/>
                        <div className="wcu-stat-text">
                            <strong>0 Missed pickups</strong>
                            <small>Last 12 months</small>
                        </div>
                    </div>
                </div>
            </div>

            <ul className="wcu-perks">
                {WHY_PERKS.map((p, i) => (
                    <PerkRow key={i} perk={p} onPerkAction={onPerkAction}/>
                ))}
            </ul>

            <div className="wcu-live">
                <div className="wcu-live-row">
                    <div className="wcu-live-text">
                        <span className="wcu-live-tag">LIVE TRACKING</span>
                        <strong>Your ride is<br/>on the way</strong>
                        <div className="wcu-live-sub">
                            <small className="lg">Real time updates</small>
                            <small>Driver location, ETA &amp; trip alerts</small>
                        </div>
                        {/* Live tracking needs the app, so this CTA is the same Track Driver
                            handoff used after booking — and it now renders EVERYWHERE, where
                            it used to be hidden outside the native app. Desktop can't run the
                            app, so it opens the Track Driver popup (the phone mock-up + store
                            badges); a phone goes straight to the store. */}
                        <TrackDriverButton className="wcu-live-cta">Get the app</TrackDriverButton>
                    </div>
                    <div className="wcu-live-phone">
                        <img src={img("live-tracking-phone.png")} alt="Live map"/>
                    </div>
                </div>
            </div>
        </div>
    );
}
